import React, { useEffect } from "react";
import { Input, type InputProps } from "../Input/Input";

export interface AuthShellProps {
    subtitle?: React.ReactNode;
    brandAlt?: string;
    logoSrc?: string;
    children: React.ReactNode;
}

/** Unified authentication shell for sign-in / registration across all Maeiz panels. */
export const AuthShell: React.FC<AuthShellProps> = ({
    subtitle,
    brandAlt = "Maeiz",
    logoSrc = "/favicon-96x96.png",
    children,
}) => {
    useEffect(() => {
        const root = document.documentElement;
        const hadDark = root.classList.contains("dark");
        root.classList.add("dark");
        return () => {
            if (!hadDark) {
                root.classList.remove("dark");
            }
        };
    }, []);

    return (
        <div className="dark flex min-h-screen items-center justify-center bg-neutral-dark p-6 text-white">
            <div className="w-full max-w-[420px]">
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-primary to-gold p-[6px] shadow-mz-lg">
                    <div className="rounded-3xl bg-neutral-dark p-6 sm:p-8">
                        <div className="mb-6 text-center">
                            <h1 className="flex items-center justify-center">
                                <img src={logoSrc} alt={brandAlt} />
                            </h1>
                            {subtitle && (
                                <p className="mt-2 text-sm font-medium text-neutral-light/60">
                                    {subtitle}
                                </p>
                            )}
                        </div>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const AuthCard = AuthShell;

export const AuthInput = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => (
    <Input
        ref={ref}
        fullWidth
        labelClassName="!text-neutral-light"
        inputTextClassName="text-white placeholder:text-white/45"
        {...props}
    />
));

AuthInput.displayName = "AuthInput";
